import { AxiosError } from 'axios'
import { useCallback, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache } from 'src/api/localities'
import { IBulkSendToLocalityRequest, invalidateMainComponentCache, useBulkSendToLocality } from 'src/api/main-component'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { invalidatePositionCache } from 'src/api/positions'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { NO_VALID_CERTIFICATE } from 'src/constant'
import { ICreateCertificate } from 'src/generated-types/Certificate'
import { validatePositionAndInstallation } from 'src/utils/function'

export interface UseSendToLocalityDialogSubmitOptions {
  customerId: EntityId
  /** Used for cache invalidation when form has no locality (e.g. list view) */
  fallbackLocalityId?: EntityId
  searchText?: string
  onSuccess?: () => void
}

/**
 * Submit handler for {@link SendToLocalityDialog}. Call {@link prepareEquipmentId} with the
 * target main component id immediately before opening the dialog.
 */
export function useSendToLocalityDialogSubmit({
  customerId,
  fallbackLocalityId,
  searchText = '',
  onSuccess,
}: UseSendToLocalityDialogSubmitOptions) {
  const { t: translate } = useTranslation(['main-component', 'common', 'service-station'])
  const equipmentIdsRef = useRef<EntityId[]>([])

  const { mutateAsync: addMainComponentToLocality } = useBulkSendToLocality(customerId)

  const prepareEquipmentId = useCallback((ids: EntityId[]) => {
    equipmentIdsRef.current = ids
  }, [])

  const onSubmit = useCallback(
    async (form: Record<string, unknown>, pop: () => void, formContext?: UseFormReturn<any>) => {
      if (!validatePositionAndInstallation(form, translate, formContext)) return
      const localityId = (form.selectedLocalityId as EntityId) ?? fallbackLocalityId
      const formSchemaReports = form.formSchemaReports as IFormSchemaReport[] | undefined
      const equipmentIds = equipmentIdsRef.current
      const formData: IBulkSendToLocalityRequest = {
        items: equipmentIds.map(id => ({
          id: id as number,
          localityId: localityId as number,
          formSchemaReports: formSchemaReports,
        })),
        selectedLocalityId: localityId as number,
        selectedPositionId: form.selectedPositionId as number,
        installationDate: form.effectiveDate as Date | string | undefined,
        certificate: form.certificate as ICreateCertificate | undefined,
      }
      await addMainComponentToLocality(formData, {
        onSuccess: () => {
          invalidateMainComponentCache.getMainComponents(
            customerId,
            form.selectedLocalityId as EntityId,
            form.selectedMooringId as EntityId,
            form.selectedPositionId as EntityId,
          )
          invalidateMainComponentCache.getOutgoingMainComponents(customerId, form.selectedLocalityId as EntityId)
          invalidateCertificateCache.getCertificatesByPosition(customerId, form.selectedPositionId as EntityId)
          invalidatePositionCache.getCagePositionDeviations(
            customerId,
            form.selectedLocalityId as EntityId,
            form.selectedPositionId as EntityId,
          )
          const typeId = form.mainComponentTypeId as EntityId | undefined
          if (typeId != null) {
            invalidateMainComponentCache.getMainComponentsByComponentType(customerId, typeId)
          }
          invalidateMainComponentCache.getMainComponentById(customerId, form.mainComponentId as EntityId)
          invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
          invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId as EntityId)
          invalidateCustomer.getCustomersActivityCounts(customerId)
          invalidationActivityLogCache.getCageActivityLogs()
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
          invalidateDocument.getDocuments(customerId)
          invalidateDocument.getDocumentsForApproval(customerId, localityId as EntityId)
          invalidateLocalityCache.getCageStatus(customerId, form.selectedLocalityId as EntityId)
          invalidateLocalityCache.getLocality(customerId, form.selectedLocalityId as EntityId)
          toast.success(translate('successfully-sent-to-locality', { ns: 'main-component' }))
          onSuccess?.()
          pop()
        },
        onError: (error: AxiosError<any, any>) => {
          if (error?.response?.data === 'ExceedMaxCountForCage') {
            toast(translate('exceedMaxCountForCage', { ns: 'main-component' }), { type: 'error' })
          }
          if (error?.response?.data === NO_VALID_CERTIFICATE) {
            toast(translate('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
          }
          if (error?.response?.data === 'CertificateNumberExists') {
            toast(translate('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
            const fc = formContext as { setError?: (n: string, o: object) => void } | undefined
            fc?.setError?.('certificateNumber', {
              type: 'manual',
              message: translate('certificateNumberExists', { ns: 'main-component' }),
            })
          }
        },
      })
    },
    [customerId, fallbackLocalityId, searchText, translate, addMainComponentToLocality, onSuccess],
  )

  return { onSubmit, prepareEquipmentId }
}
